import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, HelpCircle, Pill, MessageCircle, Thermometer, ArrowRight } from "lucide-react";
import MillionMileTicker from "@/components/MillionMileTicker";
import API from "@/lib/api";
import PublicFooter from "@/components/PublicFooter";
import LanguageToggle from "@/components/LanguageToggle";
import StickyComparePlansFab from "@/components/StickyComparePlansFab";

/**
 * Public FAQ page — accessible at /faq without authentication.
 *
 * - Pulls live, admin-editable entries from `GET /api/faq`.
 * - Renders an accordion (only one open at a time).
 * - Linked to from the landing page CTA, every footer, and the patient
 *   profile so users can reach it from anywhere in the app.
 */
export default function FAQPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let live = true;
    API.get("/faq")
      .then(({ data }) => { if (live) setItems(data || []); })
      .catch(() => { /* fail-open: empty list */ })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);

  // Inject JSON-LD `FAQPage` schema into the document <head> so Google
  // can show our questions as rich snippets directly in search results.
  // Re-runs whenever the FAQ list changes; cleans up on unmount so the
  // schema doesn't leak into other pages. Uses only ACTIVE entries (which
  // is what `/api/faq` already returns) so hidden drafts aren't indexed.
  useEffect(() => {
    if (!items.length) return undefined;
    const TAG_ID = "faq-jsonld";
    const stripHtml = (s) => (s || "").replace(/<[^>]*>/g, "").trim();
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: items.map((it) => ({
        "@type": "Question",
        name: stripHtml(it.question),
        acceptedAnswer: {
          "@type": "Answer",
          text: stripHtml(it.answer),
        },
      })),
    };
    let tag = document.getElementById(TAG_ID);
    if (!tag) {
      tag = document.createElement("script");
      tag.type = "application/ld+json";
      tag.id = TAG_ID;
      document.head.appendChild(tag);
    }
    tag.textContent = JSON.stringify(schema);
    return () => {
      const existing = document.getElementById(TAG_ID);
      if (existing) existing.remove();
    };
  }, [items]);

  const q = search.trim().toLowerCase();
  const filtered = q
    ? items.filter((i) =>
        (i.question || "").toLowerCase().includes(q) ||
        (i.answer || "").toLowerCase().includes(q))
    : items;

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50/40 via-white to-white" data-testid="faq-page">
      <MillionMileTicker />
      {/* Slim brand header — no full nav so the page focuses on questions */}
      <header className="border-b border-slate-100 bg-white/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="faq-home-link">
            <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center">
              <Pill className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-lg text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
              GLP<span className="text-purple-600">Flo</span>
            </span>
          </Link>
          <Link to="/" className="text-sm text-slate-500 hover:text-purple-700">← Back to home</Link>
        </div>
      </header>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-purple-100 text-purple-700 text-xs font-bold uppercase tracking-wider px-3 py-1">
            <HelpCircle className="w-3.5 h-3.5" /> Frequently asked
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mt-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Questions, answered.
          </h1>
          <p className="text-base text-slate-500 mt-3 max-w-xl mx-auto leading-relaxed">
            Everything you want to know about ordering, pricing, safety, and shipping at GLPFlo.
          </p>
        </div>

        {/* Search */}
        <div className="mb-6">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search FAQs…"
            className="w-full rounded-2xl border border-slate-200 px-5 py-3 text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-200"
            data-testid="faq-search-input"
          />
        </div>

        {/* Shipping & Storage callout — pulls patients with cold-chain
            anxiety straight to the full study page. SEO-friendly: includes
            the exact phrases "warm vial", "room temperature", and "GLP-1
            refrigeration" that prospective patients search for. */}
        <Link
          to="/shipping-study"
          className="block mb-8 group rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-amber-50 to-emerald-50 p-5 sm:p-6 hover:shadow-md transition-shadow"
          data-testid="faq-shipping-callout"
        >
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-amber-600 flex items-center justify-center shrink-0">
              <Thermometer className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] uppercase tracking-widest text-amber-700 font-bold">Shipping &amp; Storage FAQ</p>
              <p className="font-semibold text-slate-900 mt-0.5" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Vial arrived warm? Worried about cold-chain? Read the full stability study.
              </p>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                Compounded GLP-1 medications are stable at room temperatures up to 86&nbsp;°F for 21+ days — backed by FDA labels for Ozempic, Wegovy, Mounjaro, and Zepbound and our independent third-party HPLC stability assay.
              </p>
            </div>
            <ArrowRight className="w-5 h-5 text-amber-600 group-hover:translate-x-0.5 transition-transform shrink-0 self-center" />
          </div>
        </Link>

        {loading ? (
          <div className="text-center py-12 text-slate-400">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            No matching FAQs. Try a different search or <Link to="/" className="text-purple-600 hover:underline">contact our team</Link>.
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((item) => {
              const isOpen = open === item.id;
              return (
                <div
                  key={item.id}
                  className={`rounded-2xl border bg-white transition-all ${isOpen ? "border-purple-300 shadow-md" : "border-slate-200 shadow-sm hover:border-slate-300"}`}
                  data-testid={`faq-item-${item.id}`}
                >
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? null : item.id)}
                    className="w-full flex items-center justify-between gap-4 text-left px-5 sm:px-6 py-5"
                    aria-expanded={isOpen}
                    data-testid={`faq-toggle-${item.id}`}
                  >
                    <span className="font-bold text-base text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      {item.question}
                    </span>
                    <ChevronDown className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${isOpen ? "rotate-180 text-purple-600" : ""}`} />
                  </button>
                  {isOpen && (
                    <div className="px-5 sm:px-6 pb-5 text-sm text-slate-600 leading-relaxed whitespace-pre-line" data-testid={`faq-answer-${item.id}`}>
                      {item.answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Bottom CTA */}
        <div className="mt-12 rounded-3xl border border-purple-100 bg-gradient-to-br from-purple-50 to-pink-50 p-6 sm:p-8 text-center">
          <div className="w-12 h-12 mx-auto rounded-2xl bg-white flex items-center justify-center shadow-sm mb-3">
            <MessageCircle className="w-6 h-6 text-purple-600" />
          </div>
          <h2 className="text-base sm:text-lg font-semibold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Still have a question?
          </h2>
          <p className="text-sm text-slate-500 mt-1 mb-4">Our team is happy to help — typically replies within an hour during business hours.</p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a href="tel:+17275162142" className="rounded-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 text-sm font-semibold" data-testid="faq-call-cta">Call (727) 516-2142</a>
            <a href="tel:+18887743320" className="rounded-full bg-white border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:border-purple-300" data-testid="faq-call-cta-secondary">Or (888) 774-3320</a>
          </div>
        </div>
      </section>

      <PublicFooter />
      <StickyComparePlansFab />
    </div>
  );
}
